import { useState } from 'react';

import { Campo } from '@/components/Campo';

import { hoyMx, useOpcionesLicencia, type OtorgarBody } from './queries';

/**
 * Campos para otorgar una licencia: cuál, vigencia, obsequios y pago manual opcional (igual que
 * el legacy). Controlado: el padre guarda el valor y decide cuándo enviarlo.
 */
export function FormLicencia({
  valor,
  onCambio,
  conAviso = false,
}: {
  valor: OtorgarBody;
  onCambio: (v: OtorgarBody) => void;
  conAviso?: boolean;
}) {
  const opciones = useOpcionesLicencia();
  const [conPago, setConPago] = useState(valor.payment !== undefined);
  const elegida = opciones.data?.find((o) => o.id === valor.membershipId);
  const regalos = (elegida?.gifts ?? [])
    .map((g) => opciones.data?.find((o) => o.id === g)?.name)
    .filter(Boolean);
  const activas = opciones.data?.filter((o) => o.isActive) ?? [];
  const poner = (cambios: Partial<OtorgarBody>) => onCambio({ ...valor, ...cambios });

  return (
    <div className="space-y-4">
      <Campo etiqueta="Licencia">
        <select
          className="input-vidrio"
          value={valor.membershipId || ''}
          onChange={(e) => poner({ membershipId: Number(e.target.value) })}
          required
        >
          <option value="">Elige una licencia…</option>
          {activas.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} · ${Number(o.price).toLocaleString('es-MX')}
            </option>
          ))}
        </select>
      </Campo>
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta="Desde">
          <input
            type="date"
            className="input-vidrio"
            value={valor.startsOn}
            onChange={(e) => poner({ startsOn: e.target.value })}
            required
          />
        </Campo>
        <Campo etiqueta="Hasta">
          <input
            type="date"
            className="input-vidrio"
            value={valor.endsOn}
            min={valor.startsOn}
            onChange={(e) => poner({ endsOn: e.target.value })}
            required
          />
        </Campo>
      </div>
      {regalos.length > 0 && (
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-[var(--color-azul-600)]"
            checked={valor.includeGifts ?? true}
            onChange={(e) => poner({ includeGifts: e.target.checked })}
          />
          <span>
            Incluir obsequios con las mismas fechas: <strong>{regalos.join(', ')}</strong>
          </span>
        </label>
      )}
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[var(--color-azul-600)]"
          checked={conPago}
          onChange={(e) => {
            setConPago(e.target.checked);
            const resto = { ...valor };
            delete resto.payment;
            onCambio(
              e.target.checked
                ? {
                    ...resto,
                    payment: {
                      amount: elegida?.price ?? '',
                      method: 'Transferencia',
                      paidOn: hoyMx(),
                    },
                  }
                : resto,
            );
          }}
        />
        Registrar un pago manual
      </label>
      {conPago && valor.payment && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo etiqueta="Monto (MXN)">
            <input
              className="input-vidrio"
              inputMode="decimal"
              value={valor.payment.amount}
              onChange={(e) =>
                valor.payment && poner({ payment: { ...valor.payment, amount: e.target.value } })
              }
            />
          </Campo>
          <Campo etiqueta="Forma de pago">
            <input
              className="input-vidrio"
              list="formas-pago"
              value={valor.payment.method}
              onChange={(e) =>
                valor.payment && poner({ payment: { ...valor.payment, method: e.target.value } })
              }
            />
            <datalist id="formas-pago">
              <option value="Transferencia" />
              <option value="Depósito" />
              <option value="Efectivo" />
              <option value="PayPal" />
              <option value="Tarjeta (terminal)" />
            </datalist>
          </Campo>
          <Campo etiqueta="Fecha de pago">
            <input
              type="date"
              className="input-vidrio"
              value={valor.payment.paidOn}
              onChange={(e) =>
                valor.payment && poner({ payment: { ...valor.payment, paidOn: e.target.value } })
              }
            />
          </Campo>
        </div>
      )}
      {conAviso && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--color-azul-600)]"
            checked={valor.notify ?? false}
            onChange={(e) => poner({ notify: e.target.checked })}
          />
          Avisar al alumno por correo
        </label>
      )}
    </div>
  );
}

export function licenciaVacia(): OtorgarBody {
  return { membershipId: 0, startsOn: hoyMx(), endsOn: '', includeGifts: true, notify: false };
}
